import { createBrowserRouter, redirect } from 'react-router-dom'
import { AppLayout } from '@/components/layout/AppLayout'
import {
  protectedLandingLoader,
  protectedRouteLoader,
} from '@/features/session/routing/protected-route-loader'
import { RouteErrorBoundary } from './RouteErrorBoundary'
import type { ComponentType } from 'react'
import { FeatureAccessBoundary } from '@/features/authorization/components/FeatureAccessBoundary'

function withFeatureAccess(Component: ComponentType, permission: string) {
  return () => (
    <FeatureAccessBoundary permission={permission}>
      <Component />
    </FeatureAccessBoundary>
  )
}

let testWorkspaceComponent: Promise<ComponentType> | undefined

/**
 * Every view of the test workspace (Test, Collection run, Load test, History) is its own route but the same page.
 * They must share one component, or switching view remounts the page and the tab highlight cannot slide.
 */
function testWorkspaceRoute(path: string) {
  return {
    path,
    lazy: async () => {
      testWorkspaceComponent ??=
        import('@/features/publish/pages/TestWorkspacePage').then(
          ({ TestWorkspacePage }) =>
            withFeatureAccess(TestWorkspacePage, 'test.read'),
        )
      return { Component: await testWorkspaceComponent }
    },
  }
}

export const router = createBrowserRouter([
  {
    path: '/docs/*',
    loader: protectedRouteLoader,
    errorElement: <RouteErrorBoundary />,
    lazy: async () => {
      const { DocumentationPage } = await import('@/features/documentation')
      return { Component: DocumentationPage }
    },
  },
  {
    path: '/public/contract-review/consumer',
    errorElement: <RouteErrorBoundary />,
    lazy: async () => {
      const { PublicConsumerReviewPage } =
        await import('@/features/public-consumer-review')
      return { Component: PublicConsumerReviewPage }
    },
  },
  {
    path: '/',
    loader: protectedLandingLoader,
    errorElement: <RouteErrorBoundary />,
    lazy: async () => {
      const { LandingPage } =
        await import('@/features/landing/pages/LandingPage')
      return { Component: LandingPage }
    },
  },
  {
    path: '/profile',
    loader: protectedRouteLoader,
    // Switching profile tabs only changes ?tab=; don't re-run the session and
    // runtime-config checks (a network round trip) for that.
    shouldRevalidate: ({ currentUrl, nextUrl, defaultShouldRevalidate }) =>
      currentUrl.pathname === nextUrl.pathname
        ? false
        : defaultShouldRevalidate,
    errorElement: <RouteErrorBoundary />,
    lazy: async () => {
      const { AccountLayout } =
        await import('@/features/account/components/AccountLayout')
      return { Component: AccountLayout }
    },
    children: [
      {
        index: true,
        lazy: async () => {
          const { ProfilePage } =
            await import('@/features/account/pages/AccountSettingsPage')
          return { Component: ProfilePage }
        },
      },
      {
        path: 'support',
        lazy: async () => {
          const { SupportPage } =
            await import('@/features/account/pages/SupportPage')
          return { Component: SupportPage }
        },
      },
      {
        path: 'support/ticket',
        lazy: async () => {
          const { SupportTicketPage } =
            await import('@/features/account/pages/SupportTicketPage')
          return { Component: SupportTicketPage }
        },
      },
    ],
  },
  {
    element: <AppLayout />,
    loader: protectedRouteLoader,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: 'dashboard',
        lazy: async () => {
          const { DashboardPage } = await import('@/features/dashboard')
          return { Component: DashboardPage }
        },
      },
      {
        path: 'ide',
        lazy: async () => {
          const { IdeWelcomePage } = await import('@/features/code-workspace')
          return { Component: IdeWelcomePage }
        },
      },
      {
        path: 'onboard',
        lazy: async () => {
          const { OnboardingCatalogPage } =
            await import('@/features/onboarding/pages/OnboardingCatalogPage')
          return { Component: OnboardingCatalogPage }
        },
      },
      {
        path: 'onboard/:applicationRecordId',
        lazy: async () => {
          const { ApplicationDetailsPage } =
            await import('@/features/onboarding/pages/ApplicationDetailsPage')
          return { Component: ApplicationDetailsPage }
        },
      },
      {
        path: 'specs',
        loader: () => redirect('/specs/library'),
      },
      {
        path: 'specs/library',
        lazy: async () => {
          const { LibraryCatalogPage } =
            await import('@/features/spec-library/pages/LibraryCatalogPage')
          return { Component: LibraryCatalogPage }
        },
      },
      {
        path: 'specs/library/domains',
        lazy: async () => {
          const { DomainManagementPage } =
            await import('@/features/spec-library/pages/DomainManagementPage')
          return { Component: DomainManagementPage }
        },
      },
      {
        path: 'specs/library/review',
        lazy: async () => {
          const { LibraryReviewPage } =
            await import('@/features/spec-library/pages/LibraryReviewPage')
          return { Component: LibraryReviewPage }
        },
      },
      {
        path: 'specs/library/:librarySpecId',
        lazy: async () => {
          const { LibrarySpecPage } =
            await import('@/features/spec-library/pages/LibrarySpecPage')
          return { Component: LibrarySpecPage }
        },
      },
      {
        path: 'specs/library/:librarySpecId/edit',
        lazy: async () => {
          const { LibrarySpecEditorPage } =
            await import('@/features/spec-library/pages/LibrarySpecEditorPage')
          return { Component: LibrarySpecEditorPage }
        },
      },
      {
        path: 'specs/validation',
        lazy: async () => {
          const { ValidationPage } =
            await import('@/features/standards/pages/ValidationPage')
          return { Component: ValidationPage }
        },
      },
      {
        path: 'specs/validation/rule-sets/new',
        lazy: async () => {
          const { RuleSetPage } =
            await import('@/features/standards/pages/RuleSetPage')
          return { Component: RuleSetPage }
        },
      },
      {
        path: 'specs/validation/rule-sets/:ruleSetId/edit',
        lazy: async () => {
          const { RuleSetPage } =
            await import('@/features/standards/pages/RuleSetPage')
          return { Component: RuleSetPage }
        },
      },
      {
        path: 'specs/validation/rule-sets/:ruleSetId/revisions',
        lazy: async () => {
          const { RuleSetRevisionsPage } =
            await import('@/features/standards/pages/RuleSetRevisionsPage')
          return { Component: RuleSetRevisionsPage }
        },
      },
      {
        path: 'specs/modeling',
        lazy: async () => {
          const { DataModelsPage } =
            await import('@/features/data-models/pages/DataModelsPage')
          return { Component: DataModelsPage }
        },
      },
      {
        path: 'specs/modeling/new',
        lazy: async () => {
          const { DataModelCreatePage } =
            await import('@/features/data-models/pages/DataModelCreatePage')
          return { Component: DataModelCreatePage }
        },
      },
      {
        path: 'specs/modeling/:schemaId/revisions',
        lazy: async () => {
          const { DataModelRevisionsPage } =
            await import('@/features/data-models/pages/DataModelRevisionsPage')
          return { Component: DataModelRevisionsPage }
        },
      },
      {
        path: 'specs/modeling/:schemaId/use',
        lazy: async () => {
          const { UseDataModelPage } =
            await import('@/features/data-models/components/UseDataModelDialog')
          return { Component: UseDataModelPage }
        },
      },
      {
        path: 'specs/modeling/:schemaId/edit',
        lazy: async () => {
          const { DataModelCreatePage } =
            await import('@/features/data-models/pages/DataModelCreatePage')
          return { Component: DataModelCreatePage }
        },
      },
      {
        path: 'specs/modeling/:schemaId',
        lazy: async () => {
          const { DataModelDetailPage } =
            await import('@/features/data-models/pages/DataModelDetailPage')
          return { Component: DataModelDetailPage }
        },
      },
      {
        path: 'migration',
        lazy: async () => {
          const { default: MigrationLandingPage } =
            await import('@/features/migration/pages/MigrationLandingPage')
          return {
            Component: withFeatureAccess(
              MigrationLandingPage,
              'migration.read',
            ),
          }
        },
      },
      {
        path: 'migration/proxy',
        lazy: async () => {
          const { default: ProxyMigrationWizard } =
            await import('@/features/migration/components/proxy/ProxyMigrationWizard')
          return {
            Component: withFeatureAccess(
              ProxyMigrationWizard,
              'migration.read',
            ),
          }
        },
      },
      {
        path: 'migration/:type/:phase?',
        lazy: async () => {
          const { default: MigrationWizardPage } =
            await import('@/features/migration/pages/MigrationWizardPage')
          return {
            Component: withFeatureAccess(MigrationWizardPage, 'migration.read'),
          }
        },
      },
      {
        path: 'config/scm',
        lazy: async () => {
          const { default: ScmConfigPage } =
            await import('@/features/migration/pages/ScmConfigPage')
          return { Component: ScmConfigPage }
        },
      },
      {
        path: 'config/scm/add',
        lazy: async () => {
          const { default: AddScmConnectionPage } =
            await import('@/features/migration/pages/AddScmConnectionPage')
          return { Component: AddScmConnectionPage }
        },
      },
      {
        path: 'config/kong/:action?',
        lazy: async () => {
          const { KongProfilesPage } = await import('@/features/kong-config')
          return { Component: KongProfilesPage }
        },
      },
      {
        path: 'config/apigee/:action?',
        lazy: async () => {
          const { ApigeeProfilesPage } =
            await import('@/features/apigee-profiles/pages/ApigeeProfilesPage')
          return { Component: ApigeeProfilesPage }
        },
      },
      {
        path: 'management/workflows',
        lazy: async () => {
          const { WorkflowManagementPage } =
            await import('@/features/management/pages/WorkflowManagementPage')
          return {
            Component: withFeatureAccess(
              WorkflowManagementPage,
              'workflow_definition.read',
            ),
          }
        },
      },
      {
        path: 'management/workflows/new',
        lazy: async () => {
          const { WorkflowEditorPage } =
            await import('@/features/management/pages/WorkflowEditorPage')
          return {
            Component: withFeatureAccess(
              WorkflowEditorPage,
              'workflow_definition.write',
            ),
          }
        },
      },
      {
        path: 'management/workflows/:workflowId',
        lazy: async () => {
          const { WorkflowDetailPage } =
            await import('@/features/management/pages/WorkflowDetailPage')
          return {
            Component: withFeatureAccess(
              WorkflowDetailPage,
              'workflow_definition.read',
            ),
          }
        },
      },
      {
        path: 'management/workflows/:workflowId/edit',
        lazy: async () => {
          const { WorkflowEditorPage } =
            await import('@/features/management/pages/WorkflowEditorPage')
          return {
            Component: withFeatureAccess(
              WorkflowEditorPage,
              'workflow_definition.write',
            ),
          }
        },
      },
      {
        path: 'management/audit-logs',
        lazy: async () => {
          const { AuditLogsPage } =
            await import('@/features/management/pages/AuditLogsPage')
          return {
            Component: withFeatureAccess(AuditLogsPage, 'audit.read'),
          }
        },
      },
      // The previous CI/CD page is retired; CI/CD Automation now lives at /management/cicd.
      // {
      //   path: 'management/cicd',
      //   lazy: async () => {
      //     const { CicdAutomationPage } =
      //       await import('@/features/management/pages/CicdAutomationPage')
      //     const { CicdAccessBoundary } =
      //       await import('@/features/management/components/CicdAccessBoundary')
      //     return {
      //       Component: () => (
      //         <CicdAccessBoundary>
      //           <CicdAutomationPage />
      //         </CicdAccessBoundary>
      //       ),
      //     }
      //   },
      // },
      {
        // Bookmarks of the earlier address keep working.
        path: 'management/cicd-automation/*',
        loader: ({ request }) => {
          const url = new URL(request.url)
          return redirect(
            url.pathname.replace(
              '/management/cicd-automation',
              '/management/cicd',
            ) + url.search,
          )
        },
      },
      {
        path: 'management/connectors',
        lazy: async () => {
          const { ConnectorsLayout } =
            await import('@/features/cicd-automation')
          const { CicdAccessBoundary } =
            await import('@/features/management/components/CicdAccessBoundary')
          return {
            Component: () => (
              <CicdAccessBoundary>
                <ConnectorsLayout />
              </CicdAccessBoundary>
            ),
          }
        },
        children: [
          {
            index: true,
            lazy: async () => {
              const { CicdConnectionsPage } =
                await import('@/features/cicd-automation')
              return { Component: CicdConnectionsPage }
            },
          },
          {
            path: ':connectionId',
            lazy: async () => {
              const { CicdConnectionDetailPage } =
                await import('@/features/cicd-automation')
              return { Component: CicdConnectionDetailPage }
            },
          },
          {
            path: 'new',
            lazy: async () => {
              const { CicdConnectionEditorPage } =
                await import('@/features/cicd-automation')
              return { Component: CicdConnectionEditorPage }
            },
          },
          {
            path: ':connectionId/edit',
            lazy: async () => {
              const { CicdConnectionEditorPage } =
                await import('@/features/cicd-automation')
              return { Component: CicdConnectionEditorPage }
            },
          },
        ],
      },
      {
        path: 'management/cicd',
        lazy: async () => {
          const { CicdAutomationLayout } =
            await import('@/features/cicd-automation')
          const { CicdAccessBoundary } =
            await import('@/features/management/components/CicdAccessBoundary')
          return {
            Component: () => (
              <CicdAccessBoundary>
                <CicdAutomationLayout />
              </CicdAccessBoundary>
            ),
          }
        },
        children: [
          {
            index: true,
            lazy: async () => {
              const { CicdOverviewPage } =
                await import('@/features/cicd-automation')
              return { Component: CicdOverviewPage }
            },
          },
          {
            path: 'profiles',
            lazy: async () => {
              const { CicdProfilesPage } =
                await import('@/features/cicd-automation')
              return { Component: CicdProfilesPage }
            },
          },
          {
            path: 'profiles/new',
            lazy: async () => {
              const { CicdAutomationPage } =
                await import('@/features/cicd-automation/pages/CicdAutomationV2Page')
              return { Component: CicdAutomationPage }
            },
          },
          {
            path: 'profiles/:profileId/:stepSlug?',
            lazy: async () => {
              const { CicdAutomationPage } =
                await import('@/features/cicd-automation/pages/CicdAutomationV2Page')
              return { Component: CicdAutomationPage }
            },
          },
          {
            // The connectors moved out of CI/CD; links and bookmarks of the earlier address keep working.
            path: 'connections/*',
            loader: ({ request }) => {
              const url = new URL(request.url)
              return redirect(
                url.pathname.replace(
                  '/management/cicd/connections',
                  '/management/connectors',
                ) + url.search,
              )
            },
          },
          {
            path: 'applications',
            lazy: async () => {
              const { CicdApplicationsPage } =
                await import('@/features/cicd-automation')
              return { Component: CicdApplicationsPage }
            },
          },
          {
            path: 'code-reviewers',
            lazy: async () => {
              const { CicdCodeReviewersPage } =
                await import('@/features/cicd-automation')
              return { Component: CicdCodeReviewersPage }
            },
          },
          {
            path: 'code-reviewers/:applicationId',
            lazy: async () => {
              const { CicdCodeReviewerDetailPage } =
                await import('@/features/cicd-automation')
              return { Component: CicdCodeReviewerDetailPage }
            },
          },
          {
            path: 'audit',
            lazy: async () => {
              const { CicdAuditPage } =
                await import('@/features/cicd-automation')
              return { Component: CicdAuditPage }
            },
          },
          // The old address of the secret manager, for links and bookmarks made before it was renamed.
          {
            path: 'secret-stores',
            loader: () =>
              redirect('/management/connectors?kind=secret-manager'),
          },
          {
            path: 'secret-stores/new',
            loader: () =>
              redirect('/management/connectors/new?kind=secret-manager'),
          },
          {
            path: 'secret-manager',
            loader: () =>
              redirect('/management/connectors?kind=secret-manager'),
          },
          {
            path: 'secret-manager/new',
            loader: () =>
              redirect('/management/connectors/new?kind=secret-manager'),
          },
        ],
      },
      {
        path: 'management/approvals',
        lazy: async () => {
          const { ApprovalsHubPage } =
            await import('@/features/management/pages/ApprovalsHubPage')
          return {
            Component: withFeatureAccess(ApprovalsHubPage, 'approval.read'),
          }
        },
      },
      {
        path: 'management/approvals/contracts',
        lazy: async () => {
          const { ApprovalsPage } =
            await import('@/features/management/pages/ApprovalsPage')
          return {
            Component: withFeatureAccess(ApprovalsPage, 'approval.read'),
          }
        },
      },
      {
        // One service's contract approval requests, with the queue's filters and paging.
        path: 'management/approvals/contracts/resource/:resourceId',
        lazy: async () => {
          const { ResourceApprovalsPage } =
            await import('@/features/management/pages/ResourceApprovalsPage')
          return {
            Component: withFeatureAccess(
              ResourceApprovalsPage,
              'approval.read',
            ),
          }
        },
      },
      {
        path: 'management/approvals/merge-requests',
        lazy: async () => {
          const { MergeRequestApprovalsPage } =
            await import('@/features/management/pages/MergeRequestApprovalsPage')
          return {
            Component: withFeatureAccess(
              MergeRequestApprovalsPage,
              'approval.read',
            ),
          }
        },
      },
      {
        // One resource's merge requests, open ones and closed ones, the way GitHub lists a repository's.
        path: 'management/approvals/merge-requests/:resourceId/pulls',
        lazy: async () => {
          const { ResourcePullRequestsPage } =
            await import('@/features/code-review')
          return {
            Component: withFeatureAccess(
              ResourcePullRequestsPage,
              'approval.read',
            ),
          }
        },
      },
      {
        // One merge request, reviewed over several pages of its own: each tab is its own address.
        path: 'management/approvals/merge-requests/:resourceId',
        lazy: async () => {
          const { MergeRequestReviewPage } =
            await import('@/features/code-review')
          return {
            Component: withFeatureAccess(
              MergeRequestReviewPage,
              'approval.read',
            ),
          }
        },
        children: [
          {
            index: true,
            lazy: async () => {
              const { OverviewRoute } = await import('@/features/code-review')
              return { Component: OverviewRoute }
            },
          },
          {
            path: 'files',
            lazy: async () => {
              const { FilesRoute } = await import('@/features/code-review')
              return { Component: FilesRoute }
            },
          },
          {
            path: 'commits',
            lazy: async () => {
              const { CommitsRoute } = await import('@/features/code-review')
              return { Component: CommitsRoute }
            },
          },
          {
            path: 'checks',
            lazy: async () => {
              const { ChecksRoute } = await import('@/features/code-review')
              return { Component: ChecksRoute }
            },
          },
          {
            path: 'conversation',
            lazy: async () => {
              const { ConversationRoute } =
                await import('@/features/code-review')
              return { Component: ConversationRoute }
            },
          },
          {
            path: 'timeline',
            lazy: async () => {
              const { TimelineRoute } = await import('@/features/code-review')
              return { Component: TimelineRoute }
            },
          },
        ],
      },
      {
        path: 'management/approvals/:approvalId',
        lazy: async () => {
          const { ApprovalDetailPage } =
            await import('@/features/management/pages/ApprovalDetailPage')
          return {
            Component: withFeatureAccess(ApprovalDetailPage, 'approval.read'),
          }
        },
      },
      {
        path: 'publish/deploy',
        lazy: async () => {
          const { ApiDeployPage } =
            await import('@/features/publish/pages/ApiDeployPage')
          return {
            Component: withFeatureAccess(ApiDeployPage, 'deployment.read'),
          }
        },
      },
      {
        path: 'publish/deploy/history',
        lazy: async () => {
          const { ApiDeploymentHistoryPage } =
            await import('@/features/publish/pages/ApiDeploymentHistoryPage')
          return {
            Component: withFeatureAccess(
              ApiDeploymentHistoryPage,
              'deployment.read',
            ),
          }
        },
      },
      {
        path: 'publish/deploy/:resourceId',
        lazy: async () => {
          const { ApiDeploymentDetailPage } =
            await import('@/features/publish/pages/ApiDeploymentDetailPage')
          return {
            Component: withFeatureAccess(
              ApiDeploymentDetailPage,
              'deployment.read',
            ),
          }
        },
      },
      testWorkspaceRoute('publish/test'),
      testWorkspaceRoute('publish/test/history'),
      testWorkspaceRoute('publish/test/advanced'),
      testWorkspaceRoute('publish/test/collection-run'),
      testWorkspaceRoute('publish/test/performance'),
      {
        path: 'publish/governance',
        lazy: async () => {
          const { GovernancePage } =
            await import('@/features/publish/pages/GovernancePage')
          return {
            Component: withFeatureAccess(GovernancePage, 'governance.read'),
          }
        },
      },
      {
        path: 'publish/tracing/requests/:requestId',
        lazy: async () => {
          const { RequestDetailsPage } =
            await import('@/features/publish/components/tracing')
          return {
            Component: withFeatureAccess(RequestDetailsPage, 'monitoring.read'),
          }
        },
      },
      {
        path: 'publish/tracing/continuous/:traceId',
        lazy: async () => {
          const { TraceDetailsPage } =
            await import('@/features/publish/components/tracing')
          return {
            Component: withFeatureAccess(TraceDetailsPage, 'monitoring.read'),
          }
        },
      },
      {
        path: 'publish/tracing/sessions/:sessionId/traces/:traceId',
        lazy: async () => {
          const { TraceDetailsPage } =
            await import('@/features/publish/components/tracing')
          return {
            Component: withFeatureAccess(TraceDetailsPage, 'monitoring.read'),
          }
        },
      },
      {
        path: 'publish/tracing',
        lazy: async () => {
          const { TracingPage } =
            await import('@/features/publish/components/tracing')
          return {
            Component: withFeatureAccess(TracingPage, 'monitoring.read'),
          }
        },
      },
      {
        path: 'publish/monitoring',
        lazy: async () => {
          const { MonitoringPage } =
            await import('@/features/publish/pages/MonitoringPage')
          return {
            Component: withFeatureAccess(MonitoringPage, 'monitoring.read'),
          }
        },
      },
      {
        path: 'resources/:resourceType/:resourceId/activity',
        lazy: async () => {
          const { ResourceActivityPage } = await import('@/features/activity')
          return { Component: ResourceActivityPage }
        },
      },
      {
        path: 'resources/microservice',
        lazy: async () => {
          const { MicroserviceCatalogPage } =
            await import('@/features/lifecycle/resources/microservice/CatalogPage')
          return { Component: MicroserviceCatalogPage }
        },
      },
      {
        path: 'resources/microservice/:stepId',
        lazy: async () => {
          const { MicroserviceWorkflowPage } =
            await import('@/features/lifecycle/resources/microservice/WorkflowPage')
          return { Component: MicroserviceWorkflowPage }
        },
      },
      {
        path: 'resources/microservice/:resourceId/view',
        lazy: async () => {
          const { MicroserviceViewEntry } =
            await import('@/features/lifecycle/resources/microservice/MicroserviceViewEntry')
          return { Component: MicroserviceViewEntry }
        },
      },
      {
        // Where the migration screens send you to see a migrated microservice.
        path: 'migrated-microservices/:resourceId',
        lazy: async () => {
          const { MigratedMicroserviceViewPage } =
            await import('@/features/lifecycle/resources/microservice/migrated/MigratedViewPage')
          return { Component: MigratedMicroserviceViewPage }
        },
      },
      {
        path: 'resources/microservice/:resourceId/clone',
        lazy: async () => {
          const { MicroserviceClonePage } =
            await import('@/features/lifecycle/resources/microservice/cloning')
          return { Component: MicroserviceClonePage }
        },
      },
      {
        path: 'resources/microservice/:resourceId/version',
        lazy: async () => {
          const { MicroserviceVersionPage } =
            await import('@/features/lifecycle/resources/microservice/versioning')
          return { Component: MicroserviceVersionPage }
        },
      },
      {
        path: 'resources/microservice/:resourceId/deployments',
        lazy: async () => {
          const { MicroserviceDeploymentHistoryPage } =
            await import('@/features/lifecycle/resources/microservice/deployment-history')
          return { Component: MicroserviceDeploymentHistoryPage }
        },
      },
      {
        path: 'resources/microservice/:resourceId/editor',
        lazy: async () => {
          const { CodeWorkspacePage } =
            await import('@/features/code-workspace')
          return {
            Component: () => <CodeWorkspacePage resourceType="microservice" />,
          }
        },
      },
      {
        path: 'resources/microservice/:resourceId/:stepId',
        lazy: async () => {
          const { MicroserviceWorkflowPage } =
            await import('@/features/lifecycle/resources/microservice/WorkflowPage')
          return { Component: MicroserviceWorkflowPage }
        },
      },
      {
        path: 'resources/apigee-proxy',
        lazy: async () => {
          const { ApigeeProxyCatalogPage } =
            await import('@/features/lifecycle/resources/apigee-proxy/CatalogPage')
          return { Component: ApigeeProxyCatalogPage }
        },
      },
      {
        path: 'resources/apigee-proxy/:stepId',
        lazy: async () => {
          const { ApigeeProxyWorkflowPage } =
            await import('@/features/lifecycle/resources/apigee-proxy/WorkflowPage')
          return { Component: ApigeeProxyWorkflowPage }
        },
      },
      {
        path: 'resources/apigee-proxy/:resourceId/view',
        lazy: async () => {
          const { ApigeeProxyViewPage } =
            await import('@/features/lifecycle/resources/apigee-proxy/ViewPage')
          return { Component: ApigeeProxyViewPage }
        },
      },
      {
        path: 'resources/apigee-proxy/:resourceId/clone',
        lazy: async () => {
          const { ApigeeProxyClonePage } =
            await import('@/features/lifecycle/resources/apigee-proxy/cloning')
          return { Component: ApigeeProxyClonePage }
        },
      },
      {
        path: 'resources/apigee-proxy/:resourceId/version',
        lazy: async () => {
          const { ApigeeProxyVersionPage } =
            await import('@/features/lifecycle/resources/apigee-proxy/versioning')
          return { Component: ApigeeProxyVersionPage }
        },
      },
      {
        path: 'resources/apigee-proxy/:resourceId/deployments',
        lazy: async () => {
          const { ApigeeProxyDeploymentHistoryPage } =
            await import('@/features/lifecycle/resources/apigee-proxy/deployment-history')
          return { Component: ApigeeProxyDeploymentHistoryPage }
        },
      },
      {
        path: 'resources/apigee-proxy/:resourceId/editor',
        lazy: async () => {
          const { ApigeeProxyBundleEditorPage } =
            await import('@/features/apigee-bundle-editor')
          return { Component: ApigeeProxyBundleEditorPage }
        },
      },
      {
        path: 'resources/apigee-proxy/:resourceId/:stepId',
        lazy: async () => {
          const { ApigeeProxyWorkflowPage } =
            await import('@/features/lifecycle/resources/apigee-proxy/WorkflowPage')
          return { Component: ApigeeProxyWorkflowPage }
        },
      },
      {
        path: 'resources/apigee-shared-flow',
        lazy: async () => {
          const { ApigeeSharedFlowCatalogPage } =
            await import('@/features/lifecycle/resources/apigee-shared-flow/CatalogPage')
          return { Component: ApigeeSharedFlowCatalogPage }
        },
      },
      {
        path: 'resources/apigee-shared-flow/:stepId',
        lazy: async () => {
          const { ApigeeSharedFlowWorkflowPage } =
            await import('@/features/lifecycle/resources/apigee-shared-flow/WorkflowPage')
          return { Component: ApigeeSharedFlowWorkflowPage }
        },
      },
      {
        path: 'resources/apigee-shared-flow/:resourceId/view',
        lazy: async () => {
          const { ApigeeSharedFlowViewPage } =
            await import('@/features/lifecycle/resources/apigee-shared-flow/ViewPage')
          return { Component: ApigeeSharedFlowViewPage }
        },
      },
      {
        path: 'resources/apigee-shared-flow/:resourceId/clone',
        lazy: async () => {
          const { ApigeeSharedFlowClonePage } =
            await import('@/features/lifecycle/resources/apigee-shared-flow/cloning')
          return { Component: ApigeeSharedFlowClonePage }
        },
      },
      {
        path: 'resources/apigee-shared-flow/:resourceId/version',
        lazy: async () => {
          const { ApigeeSharedFlowVersionPage } =
            await import('@/features/lifecycle/resources/apigee-shared-flow/versioning')
          return { Component: ApigeeSharedFlowVersionPage }
        },
      },
      {
        path: 'resources/apigee-shared-flow/:resourceId/deployments',
        lazy: async () => {
          const { ApigeeSharedFlowDeploymentHistoryPage } =
            await import('@/features/lifecycle/resources/apigee-shared-flow/deployment-history')
          return { Component: ApigeeSharedFlowDeploymentHistoryPage }
        },
      },
      {
        path: 'resources/apigee-shared-flow/:resourceId/editor',
        lazy: async () => {
          const { ApigeeSharedFlowBundleEditorPage } =
            await import('@/features/apigee-bundle-editor')
          return { Component: ApigeeSharedFlowBundleEditorPage }
        },
      },
      {
        path: 'resources/apigee-shared-flow/:resourceId/:stepId',
        lazy: async () => {
          const { ApigeeSharedFlowWorkflowPage } =
            await import('@/features/lifecycle/resources/apigee-shared-flow/WorkflowPage')
          return { Component: ApigeeSharedFlowWorkflowPage }
        },
      },
      {
        path: 'resources/apigee-config',
        lazy: async () => {
          const { ApigeeConfigPage } = await import('@/features/apigee-config')
          return { Component: ApigeeConfigPage }
        },
      },
      {
        path: 'resources/apigee-config/:resourceKind/:resourceId',
        lazy: async () => {
          const { ApigeeConfigPage } = await import('@/features/apigee-config')
          return { Component: ApigeeConfigPage }
        },
      },
      {
        path: 'resources/kong-gateway-service',
        lazy: async () => {
          const { KongGatewayServiceCatalogPage } =
            await import('@/features/lifecycle/resources/kong-gateway-service/CatalogPage')
          return { Component: KongGatewayServiceCatalogPage }
        },
      },
      {
        path: 'resources/kong-gateway-service/:stepId',
        lazy: async () => {
          const { KongGatewayServiceWorkflowPage } =
            await import('@/features/lifecycle/resources/kong-gateway-service/WorkflowPage')
          return { Component: KongGatewayServiceWorkflowPage }
        },
      },
      {
        path: 'resources/kong-gateway-builder',
        lazy: async () => {
          const { KongConfigPage } = await import('@/features/kong-config')
          return { Component: KongConfigPage }
        },
      },
      {
        path: 'resources/kong-gateway-builder/:resourceKind/:resourceId',
        lazy: async () => {
          const { KongConfigPage } = await import('@/features/kong-config')
          return { Component: KongConfigPage }
        },
      },
      {
        path: 'resources/kong-gateway-service/:resourceId/view',
        lazy: async () => {
          const { KongGatewayServiceViewPage } =
            await import('@/features/lifecycle/resources/kong-gateway-service/ViewPage')
          return { Component: KongGatewayServiceViewPage }
        },
      },
      {
        path: 'resources/kong-gateway-service/:resourceId/clone',
        lazy: async () => {
          const { KongGatewayClonePage } =
            await import('@/features/lifecycle/resources/kong-gateway-service/cloning')
          return { Component: KongGatewayClonePage }
        },
      },
      {
        path: 'resources/kong-gateway-service/:resourceId/version',
        lazy: async () => {
          const { KongGatewayVersionPage } =
            await import('@/features/lifecycle/resources/kong-gateway-service/versioning')
          return { Component: KongGatewayVersionPage }
        },
      },
      {
        path: 'resources/kong-gateway-service/:resourceId/deployments',
        lazy: async () => {
          const { KongGatewayDeploymentHistoryPage } =
            await import('@/features/lifecycle/resources/kong-gateway-service/deployment-history')
          return { Component: KongGatewayDeploymentHistoryPage }
        },
      },
      {
        path: 'resources/kong-gateway-service/:resourceId/editor',
        lazy: async () => {
          const { CodeWorkspacePage } =
            await import('@/features/code-workspace')
          return {
            Component: () => (
              <CodeWorkspacePage resourceType="kong-gateway-service" />
            ),
          }
        },
      },
      {
        path: 'resources/kong-gateway-service/:resourceId/:stepId',
        lazy: async () => {
          const { KongGatewayServiceWorkflowPage } =
            await import('@/features/lifecycle/resources/kong-gateway-service/WorkflowPage')
          return { Component: KongGatewayServiceWorkflowPage }
        },
      },
      {
        path: 'resources/forgesphere-proxy',
        lazy: async () => {
          const { ForgeSphereProxyCatalogPage } =
            await import('@/features/lifecycle/resources/forgesphere-proxy/CatalogPage')
          return { Component: ForgeSphereProxyCatalogPage }
        },
      },
      {
        path: 'resources/forgesphere-proxy/:stepId',
        lazy: async () => {
          const { ForgeSphereProxyWorkflowPage } =
            await import('@/features/lifecycle/resources/forgesphere-proxy/WorkflowPage')
          return { Component: ForgeSphereProxyWorkflowPage }
        },
      },
      {
        path: 'resources/forgesphere-config',
        lazy: async () => {
          const { ForgeSphereConfigPage } =
            await import('@/features/forgesphere-config')
          return { Component: ForgeSphereConfigPage }
        },
      },
      {
        path: 'resources/forgesphere-config/:resourceKind/:resourceId',
        lazy: async () => {
          const { ForgeSphereConfigPage } =
            await import('@/features/forgesphere-config')
          return { Component: ForgeSphereConfigPage }
        },
      },
      {
        path: 'resources/forgesphere-proxy/:resourceId/view',
        lazy: async () => {
          const { ForgeSphereGatewayViewPage } =
            await import('@/features/lifecycle/resources/forgesphere-proxy/ViewPage')
          return { Component: ForgeSphereGatewayViewPage }
        },
      },
      {
        path: 'resources/forgesphere-proxy/:resourceId/clone',
        lazy: async () => {
          const { ForgeSphereGatewayClonePage } =
            await import('@/features/lifecycle/resources/forgesphere-proxy/cloning')
          return { Component: ForgeSphereGatewayClonePage }
        },
      },
      {
        path: 'resources/forgesphere-proxy/:resourceId/version',
        lazy: async () => {
          const { ForgeSphereGatewayVersionPage } =
            await import('@/features/lifecycle/resources/forgesphere-proxy/versioning')
          return { Component: ForgeSphereGatewayVersionPage }
        },
      },
      {
        path: 'resources/forgesphere-proxy/:resourceId/deployments',
        lazy: async () => {
          const { ForgeSphereGatewayDeploymentHistoryPage } =
            await import('@/features/lifecycle/resources/forgesphere-proxy/deployment-history')
          return { Component: ForgeSphereGatewayDeploymentHistoryPage }
        },
      },
      {
        path: 'resources/forgesphere-proxy/:resourceId/:stepId',
        lazy: async () => {
          const { ForgeSphereProxyWorkflowPage } =
            await import('@/features/lifecycle/resources/forgesphere-proxy/WorkflowPage')
          return { Component: ForgeSphereProxyWorkflowPage }
        },
      },
      {
        path: 'resources/forgesphere-function',
        lazy: async () => {
          const { ForgeSphereFunctionCatalogPage } =
            await import('@/features/lifecycle/resources/forgesphere-function/CatalogPage')
          return { Component: ForgeSphereFunctionCatalogPage }
        },
      },
      {
        path: 'resources/forgesphere-function/:stepId',
        lazy: async () => {
          const { ForgeSphereFunctionWorkflowPage } =
            await import('@/features/lifecycle/resources/forgesphere-function/WorkflowPage')
          return { Component: ForgeSphereFunctionWorkflowPage }
        },
      },
      {
        path: 'resources/forgesphere-function/:resourceId/view',
        lazy: async () => {
          const { ForgeSphereFunctionViewPage } =
            await import('@/features/lifecycle/resources/forgesphere-function/ViewPage')
          return { Component: ForgeSphereFunctionViewPage }
        },
      },
      {
        path: 'resources/forgesphere-function/:resourceId/clone',
        lazy: async () => {
          const { ForgeSphereFunctionClonePage } =
            await import('@/features/lifecycle/resources/forgesphere-function/cloning')
          return { Component: ForgeSphereFunctionClonePage }
        },
      },
      {
        path: 'resources/forgesphere-function/:resourceId/version',
        lazy: async () => {
          const { ForgeSphereFunctionVersionPage } =
            await import('@/features/lifecycle/resources/forgesphere-function/versioning')
          return { Component: ForgeSphereFunctionVersionPage }
        },
      },
      {
        path: 'resources/forgesphere-function/:resourceId/deployments',
        lazy: async () => {
          const { ForgeSphereFunctionDeploymentHistoryPage } =
            await import('@/features/lifecycle/resources/forgesphere-function/deployment-history')
          return { Component: ForgeSphereFunctionDeploymentHistoryPage }
        },
      },
      {
        path: 'resources/forgesphere-function/:resourceId/:stepId',
        lazy: async () => {
          const { ForgeSphereFunctionWorkflowPage } =
            await import('@/features/lifecycle/resources/forgesphere-function/WorkflowPage')
          return { Component: ForgeSphereFunctionWorkflowPage }
        },
      },
      {
        path: 'resources/mcp',
        lazy: async () => {
          const { McpCatalogPage } =
            await import('@/features/lifecycle/resources/mcp/CatalogPage')
          return { Component: McpCatalogPage }
        },
      },
      {
        path: 'resources/mcp/:stepId',
        lazy: async () => {
          const { McpWorkflowPage } =
            await import('@/features/lifecycle/resources/mcp/WorkflowPage')
          return { Component: McpWorkflowPage }
        },
      },
      {
        path: 'resources/mcp/:resourceId/view',
        lazy: async () => {
          const { McpViewPage } =
            await import('@/features/lifecycle/resources/mcp/ViewPage')
          return { Component: McpViewPage }
        },
      },
      {
        path: 'resources/mcp/:resourceId/clone',
        lazy: async () => {
          const { McpClonePage } =
            await import('@/features/lifecycle/resources/mcp/cloning')
          return { Component: McpClonePage }
        },
      },
      {
        path: 'resources/mcp/:resourceId/version',
        lazy: async () => {
          const { McpVersionPage } =
            await import('@/features/lifecycle/resources/mcp/versioning')
          return { Component: McpVersionPage }
        },
      },
      {
        path: 'resources/mcp/:resourceId/deployments',
        lazy: async () => {
          const { McpDeploymentHistoryPage } =
            await import('@/features/lifecycle/resources/mcp/deployment-history')
          return { Component: McpDeploymentHistoryPage }
        },
      },
      {
        path: 'resources/mcp/:resourceId/editor',
        lazy: async () => {
          const { CodeWorkspacePage } =
            await import('@/features/code-workspace')
          return { Component: () => <CodeWorkspacePage resourceType="mcp" /> }
        },
      },
      {
        path: 'resources/mcp/:resourceId/:stepId',
        lazy: async () => {
          const { McpWorkflowPage } =
            await import('@/features/lifecycle/resources/mcp/WorkflowPage')
          return { Component: McpWorkflowPage }
        },
      },
    ],
  },
  {
    path: '*',
    loader: protectedRouteLoader,
    errorElement: <RouteErrorBoundary />,
    lazy: async () => {
      const { NotFoundPage } = await import('@/features/not-found/NotFoundPage')
      return { Component: NotFoundPage }
    },
  },
])
